import {
  accentButtonBackground,
  accentButtonBorder,
  accentButtonHoverBackground,
  brandButtonBackground,
  brandButtonBorder,
  brandButtonHoverBackground,
  BreakpointDown,
  BreakpointUp,
  darkPalette,
  modalBackground,
  primaryBorder,
  readableColor,
  scrollbars,
  shadow,
  styledMedia,
  styledSourceType,
  styledThemeName,
  styledTypeAndTheme,
  syntax,
  ThemeProps,
} from "@kaggle/core";
import { KAGGLE_BLACK, KAGGLE_WHITE } from "@kaggle/material";
import { createGlobalStyle } from "styled-components";

const DARK_EDIT_BG = "#0A1116";
const DARK_EDIT_BORDER = "#4f5868";
const LIGHT_CELL_BACKGROUND = "#F7F7F7";

/**
 * Note: These look lifted from Jupyter to match their styles, and is why they
 * are an odd numbers.
 */
const CELL_VERTICAL_REM_BASIS = 2.857142;
const CELL_CONTENT_VERTICAL_REM_BASIS = 2.285714;
/** The cell spacing configuration. */
export const CELL_SPACING = {
  left: "50px",
  right: "8px",
  leftMargin: "5px",
  rightMargin: "5px",
  vertical: `${CELL_VERTICAL_REM_BASIS}rem`,
  verticalHalf: `${CELL_VERTICAL_REM_BASIS / 2}rem`,
  contentVertical: `${CELL_CONTENT_VERTICAL_REM_BASIS}rem`,
  contentVerticalHalf: `${CELL_CONTENT_VERTICAL_REM_BASIS / 2}rem`,
};

/** A special class name we add to the Jupyter cell when it is collapsed. */
export const CLASS_NAME_COLLAPSED = "_kg_hide-input";

/** A special class name we add to the Jupyter cell when it is frozen. */
export const CLASS_NAME_FROZEN = "_kg_frozen";

/** A special class name we add to the Jupyter cell output when it is collapsed. */
export const CLASS_NAME_COLLAPSED_OUTPUT = "_kg_hide-output";

/** Global styles to apply to Jupyter notebook. */
export const NotebookGlobalStyle = createGlobalStyle`

#ipython-main-app {
    position: relative;
}

#site {
  padding: 0;
}

body > div#site::-webkit-scrollbar {
    width: 12px;
}

/* Hide the notebooks default header permanently. */
body > #header {
  display: none !important;
}

/* All items set to Roboto */
code,
pre,
.cell code,
.cell pre,
.prompt {
  line-height: 1.4285;
  font-family: "Roboto Mono", monospace;
  font-weight: 400;

  ${styledMedia(BreakpointUp.XXS)`
    font-size: 13px;
  `}

  ${styledMedia(BreakpointDown.XXS)`
    font-size: 12px;
  `}
}

/* Hide the default Jupyter left cell prompt. */
div.prompt_container {
  display: none !important;
}

/* When multiple cells are selected, sets the style. */
div.cell.jupyter-soft-selected {
  margin-right: calc(${CELL_SPACING.rightMargin} - 0.5rem) !important;

  &:first-child {
    margin-top: 0.5rem !important;
  }

  &:last-child {
    margin-bottom: 1rem !important;
  }

  ${styledThemeName("dark")`
    background: #4F5868 !important;
  `}
}

/* Set output cell prompts (where they show up) to look similar in size to input prompts. */
.prompt.output_prompt bdi {
  font-size: 10px;
}

/* When insert key is pressed, notebook enters overtype mode. Use this to style the cursor. */
.CodeMirror-overwrite .CodeMirror-cursor {
  ${styledThemeName("light")`
    background-color: black;
  `}
  ${styledThemeName("dark")`
    background-color: white;
  `}
  width: auto;
  color: white;
  border-left: 0 !important;
  line-height: normal;
  white-space: pre;
  opacity: 0.5;
}

/*
Addresses Chrome flexbox rendering change introduced in v72:
https://bugs.chromium.org/p/chromium/issues/detail?id=927066
*/
div.inner_cell {
  min-height: 0;
}

${styledSourceType("notebook")`
  /* Avoids overlapping with the left border. */
  .CodeMirror-hscrollbar {
    left: 1px !important;
  }

  .notebook_app.command_mode div.text_cell.selected .CodeMirror-wrap {
    border-bottom: 1px solid transparent !important;
  }

  .notebook_app.edit_mode div.text_cell.selected .CodeMirror-wrap {
    border-bottom: 1px solid #DEDFE0 !important;
    border-right: 1px solid #DEDFE0 !important;
  }

  div.input_area {
    border-top-right-radius: 4px !important;
    border-bottom-right-radius: 4px !important;
  }

  .notebook_app #notebook #notebook-container div.input_area {
    /* !important required to override Jupyter's use of !important here */
    border: 1px solid transparent !important;
  }
`}

/* Selected+edit mode background and border colors for Light Mode. */
${styledTypeAndTheme("notebook", "light")`

  .notebook_app.edit_mode #notebook #notebook-container div.code_cell.selected div.input_area,
  .notebook_app.edit_mode #notebook #notebook-container div.text_cell.unrendered.selected div.input_area .CodeMirror-wrap {
    background: ${KAGGLE_WHITE};
  }

  .notebook_app.edit_mode #notebook #notebook-container div.code_cell.selected div.input_area {
    /* !important required to override Jupyter's use of !important here */
    border-color: #DEDFE0 !important;
  }
`}

/* Selected&edit mode background and border colors for Dark Mode. */
${styledTypeAndTheme("notebook", "dark")`
  .notebook_app.edit_mode #notebook #notebook-container div.code_cell.selected div.input_area,
  .notebook_app.edit_mode #notebook #notebook-container div.text_cell.unrendered.selected div.input_area .CodeMirror-wrap {
    background: ${DARK_EDIT_BG};
  }

  .notebook_app.edit_mode #notebook #notebook-container div.code_cell.selected div.input_area,
  .notebook_app.edit_mode div.text_cell.selected .CodeMirror-wrap {
    /* !important required to override Jupyter's use of !important here */
    border-color: ${DARK_EDIT_BORDER} !important;
  }
`}

/** FIX(b/138795776): Themed RMarkdown output. */
.output > .output_area > .rendered_html {
  ${readableColor(100)};
}

span.cm-comment,
.cm-s-ipython span.cm-comment {
  font-style: normal;
}

.input_area {
  /* Requires important to override Jupyter inline styles. */
  border-radius: 0 !important;

  /* Requires important to override Jupyter inline styles. */
  border: 0 !important;
}

.cell.text_cell.unselected.rendered.${CLASS_NAME_FROZEN} {
  pointer-events: none;
}

/* Allow <a> links in frozen markdown cells to be followed. */
.cell.text_cell.unselected.rendered.${CLASS_NAME_FROZEN} a {
  pointer-events: auto;
}

/* Disable Jupyter kernel modal. */
.kernel-modal {
  display: none !important;
}

/* Style Jupyter internal modals to look like Kaggle modals. */
.modal .modal-dialog {
  .modal-header {
    ${primaryBorder("border-bottom")};
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  }

  .modal-content {
    ${modalBackground()};
    ${readableColor()};
    ${shadow()};
    border-radius: 0;
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;

    p {
      ${readableColor(70)};
      font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    }
  }

  .modal-footer {
    ${primaryBorder("border-top")};
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  }

  /* Button style overrides pulled from material button base. */
  button.btn {
    ${shadow()};
    color: #fff;
    min-width: 64px;
    box-sizing: border-box;
    transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    font-weight: 500;
    line-height: 1.75;
    border-radius: 4px;
    letter-spacing: 0.02857em;

    &:focus {
      outline: none;
    }
  }

  /* Override default button colors. */
  button.btn-default:not(.btn-danger) {
    ${brandButtonBackground()};
    ${brandButtonBorder()};

    &:hover {
      ${brandButtonHoverBackground()};
    }
  }

  button.btn-danger {
    ${accentButtonBackground("error")};
    ${accentButtonBorder("border", "error")};

    &:hover {
      ${accentButtonHoverBackground("error")};
    }
  }

  button.close {
    ${readableColor(70)};
  }
}

${styledSourceType("notebook")`
  /* Fixes weird rendering at small widths. */
  #notebook-container div.input { 
    flex-direction: row;
  }
`}


${styledSourceType("script")`
/* Hide all but the first cell in scripts. */
div.cell.code_cell:nth-child(n+2) {
  display: none !important;
}

.CodeMirror-gutters {
  padding-right: 4px;
  border-right: 1px solid ${darkPalette.lightLines};
  /* Make it so scroll bar gutter doesn't jitter on horizontal move (default absolute). */
  position: fixed !important;
  left: 0 !important;
}

.end_space {
  display: none !important;
}
`}

${styledThemeName("light")`
/* Fix the markdown toolbar. */
#notebook .mirrormark-toolbar {
    background: ${KAGGLE_WHITE};
    border: 1px solid #dedfe0;
    color: ${KAGGLE_BLACK};
}

/* Fix the markdown toolbar buttons. */
#notebook .mirrormark-toolbar li a {
    background: ${KAGGLE_WHITE};
    color: ${KAGGLE_BLACK};

    &:hover {
      background: rgba(0, 0, 0, 0.2);
    }
}

/* Ensure cell background on empty markdown cell. */
.CodeMirror-wrap {
  background: ${LIGHT_CELL_BACKGROUND};
}

/* The markdown cell. */
#notebook #notebook-container div.text_cell.unrendered.selected div.input_area .CodeMirror-wrap {
  background: ${LIGHT_CELL_BACKGROUND};
}


/* Markdown headers (edit mode). */
#notebook .cm-s-default .cm-header  {
  color: ${KAGGLE_BLACK};
}

/* Fix code colors in rendered markdown. */
#notebook #notebook-container div.text_cell .rendered_html pre,
#notebook #notebook-container div.text_cell .rendered_html code {
  background: ${LIGHT_CELL_BACKGROUND} !important;
  color: rgba(0, 0, 0, 1) !important;
}
`}

${styledThemeName("dark")`

.CodeMirror-linenumber {
  color: ${darkPalette.lineNumbers};
}

span.cm-keyword {
  color: ${syntax.dark.green} !important;
}

span.cm-builtin {
  color: ${syntax.dark.cyan} !important;
}

span.cm-comment {
  color: ${syntax.dark.black} !important;
}

span.cm-string {
  color: ${syntax.dark.red} !important;
}

span.cm-def {
  color: ${syntax.dark.blue} !important;
}

span.cm-variable {
  color: ${syntax.dark.white} !important;
}

/* self is highlighted with this in python. */
span.cm-variable-2 {
  color: ${syntax.dark.yellow} !important;
}

span.cm-operator {
  color: ${syntax.dark.magenta} !important;
}

span.cm-property {
  color: ${syntax.dark.white} !important;
}

.CodeMirror, .CodeMirror-code {
  color: rgba(255, 255, 255, 0.5) !important;
}

.CodeMirror-cursor {
    border-left: 1.4px solid ${darkPalette.text[100]} !important;
}

/* Markdown */
div.text_cell_render {
  color: ${darkPalette.text[100]} !important;
}

/* Ensure cell background on empty markdown cell. */
.CodeMirror-wrap {
  background: ${darkPalette.cellBackground};
}

/* Bootstrap cleanup in output. */
.output_wrapper {
  .stats th {
    color: ${darkPalette.text[100]};
    background-color: ${darkPalette.windowBackground};
  }

  .nav-tabs > li.active > a, .nav-tabs > li.active > a:hover, .nav-tabs > li.active > a:focus {
    color: ${darkPalette.text[100]};
    background-color: ${darkPalette.accent};
  }

  div.row.headerrow {
    color: ${darkPalette.text[70]};
    background-color: ${darkPalette.cellBackground};
  }

  div.row.variablerow {
    color: ${darkPalette.text[70]};
    background-color: ${darkPalette.windowBackground};
  }
}

#notebook .rendered_html thead tr {
  background: ${darkPalette.cellBackground};
  color: ${KAGGLE_WHITE};
}

#notebook .rendered_html tbody tr:nth-child(odd) {
  background: ${darkPalette.windowBackground};
  color: ${KAGGLE_WHITE};
}

#notebook .rendered_html tbody tr:nth-child(even) {
  background: ${darkPalette.cellBackground};
  color: ${KAGGLE_WHITE};
}

/* Better looking auto-completion. */
.completions {
  ${shadow()};
  select {
    color: ${KAGGLE_WHITE};
    border: 1px solid ${darkPalette.darkLines};
    background: ${darkPalette.darkLines};
  }
}

/* Change the text selection background to be readable. */
#notebook div.CodeMirror-selected {
  background: ${darkPalette.sideBars} !important;
}

#notebook div.input_area {
  background: ${darkPalette.cellBackground};
}

/* Fix the markdown toolbar. */
#notebook .mirrormark-toolbar {
    background: ${darkPalette.windowBackground};
    border: 1px solid ${darkPalette.darkLines};
    color: ${darkPalette.text[70]};
}

/* Fix the markdown toolbar buttons. */
#notebook .mirrormark-toolbar li a {
    background: ${darkPalette.windowBackground};
    color: ${darkPalette.text[70]};

    &:hover {
      background: ${darkPalette.cellBackground};
    }
}

/* The markdown cell. */
#notebook #notebook-container div.text_cell.unrendered.selected div.input_area .CodeMirror-wrap {
  background: ${darkPalette.cellBackground};
}

/* Markdown headers (edit mode). */
#notebook .cm-s-default .cm-header  {
  color: ${darkPalette.text[100]};
}

/* Markdown anchors (edit mode). */
#notebook .cm-s-default .cm-link {
  color: ${darkPalette.text[50]};
}

/* Fix blockquotes in rendered markdown. */
#notebook #notebook-container div.text_cell .rendered_html blockquote {
  color: ${darkPalette.text[70]};
}

/* Fix code colors in rendered markdown. */
#notebook #notebook-container div.text_cell .rendered_html pre,
#notebook #notebook-container div.text_cell .rendered_html code {
  background: ${darkPalette.cellBackground} !important;
  color: ${darkPalette.text[100]} !important;
}

/* Fix help text (b/138795596). */
div#pager {
  background: ${darkPalette.windowBackground};
  ${readableColor(70)};

  h1,h2,h3,h4,h5,h6 {
    ${readableColor(100)};
  }

  .ui-icon {
    ${readableColor(100)};
  }

  .ui-resizable-handle {
    background: ${darkPalette.windowBackground};
    border-top: 1px solid ${darkPalette.darkLines};
    border-bottom: 1px solid ${darkPalette.darkLines};
  }

  pre {
    background-color: ${darkPalette.cellBackground};
    color: ${darkPalette.text[70]};
  }

  code {
    background-color: ${darkPalette.cellBackground};
    color: ${darkPalette.text[100]};
  }
}
`}

${styledThemeName("dark")`
#notebook {
  background: ${darkPalette.windowBackground};
}

.CodeMirror-gutters {
  background: ${darkPalette.windowBackground} !important;
  border-right: 1px solid ${darkPalette.darkLines};
}
`}

#notebook {
  /* Requires important to override Jupyter inline styles. */
  padding-top: 0 !important;
}

/* Remove the border around markdown cells. */
#notebook #notebook-container div.text_cell.unrendered.selected div.input_area .CodeMirror-wrap {
  border: 0;

  /* Fixes a bug in FireFox where markdown cell only goes half width. */
  min-width: calc(100vw - ${CELL_SPACING.left} - 4.5rem);
}

.notebook_app {
  /* Requires important to override Jupyter inline styles. */
  background: transparent !important;
}

.container .cell {
  margin: 0;
}

/* The injected output header wrapper style. */
.kaggle_cell_output_header {
  position: relative;
  width: 100%;
  z-index: 100;
}

.rendered_html h1,
.rendered_html h2,
.rendered_html h3,
.rendered_html h4,
.rendered_html h5,
.rendered_html h6,
.rendered_html p {
  /* Requires important to override Jupyter inline styles. */
  margin: 0 !important;
}

.rendered_html > *:first-child {
  /* Requires important to override Jupyter inline styles. */
  padding-top: 0 !important;
}

.rendered_html > *:last-child {
  /* Requires important to override Jupyter inline styles. */
  padding-bottom: 0 !important;
}

  /* Cleaner scrollbars. */
  * {
    ${props => scrollbars[props.theme.name]}
  }

  html {
    font-size: 87.5% !important; /* 12px - in iframe, 14px - outside of */
  }

  /* Replace input prompts with our custom version. */
  #notebook-container > div.cell > .input > .prompt,
  #notebook-container > div.cell > .prompt {
    display: none;
  }

  #notebook-container {
    /* Required to override Jupyter inline style. */
    box-shadow: none !important;
    /* Required to override Jupyter inline style. */
    background: transparent !important;
    /* Required to override Jupyter inline style. */
    margin: 0 !important;
    /* Required to override Jupyter inline style. */
    padding: 0 !important;
    /* Required to override Jupyter inline style. */
    width: 100% !important;
    /*
    margin-top: 3.5355rem !important;
    */
  }

  /* Selects the inner container of markdown cells which are being edited. */
  #notebook-container div.text_cell.unrendered.selected {

    div.input_area {
      /*
        * There isn't a good way to re-structure the cell node hierarchy, so use
        * the Grid Layout to get the correct arrangement of sibling nodes. 
        */
      display: grid;
      
      /* 0px first column because we do not show kaggle_cell_left for markdown. */
      grid-auto-columns: 0 auto 40px;

      .kaggle_cell_left {
        grid-column: 1;
        grid-row: 1 / 3;
      }
      .mirrormark-toolbar {
        grid-column: 2;
        grid-row: 1;
      }
      .CodeMirror-wrap {
        grid-column: 2;
        grid-row: 2;
      }
    }
  }

  #notebook-container div.code_cell.${CLASS_NAME_COLLAPSED} {
    .inner_cell {
      display: none !important;
    }

    div.kaggle_cell_collapsed {
      width: 100%;
    }
  }

  /* Hide the markdown editor and the rendered html if the text cell is collapsed. */
  #notebook-container div.text_cell.${CLASS_NAME_COLLAPSED} {
    .inner_cell {
      display: block !important;

      .input_area {
        display: block !important;
      }
    }

    .text_cell_render {
      display: none !important;
    }

    .kaggle_cell_collapsed {
      width: 100%;
    }

    .CodeMirror {
      display: none !important;
    }

    .mirrormark-toolbar {
      display: none !important;
    }
  }

  #notebook-container div.text_cell {
    font-family: "Inter", sans-serif !important;
    display: block;

    div.input_area {
      background: transparent;
    }

    .inner_cell {
      padding: 0 0 0 ${CELL_SPACING.left} !important;
    }

    .rendered_html {
      padding: 10px 8px;

      a.anchor-link:link {
        display: none !important;
      }

      h1,h2,h3,h4,h5,h6 {
        font-weight: 500;
      }

      h1,h2,h3,h4,h5,h6,p,ul,ol,blockquote,> code,> pre {
        line-height:1.4285;
        margin: 0 0 ${CELL_SPACING.contentVertical} 0 !important;
        padding: 0 !important;
        &:last-child {
          margin-bottom: 0 !important;
        }
      }

      blockquote {
        border-left: 1px solid #dedfe0;
        color: rgba(0,0,0,0.7);
        padding-left: ${CELL_SPACING.contentVerticalHalf} !important;
      }

      p {
        margin: 0 0 1.42857rem 0 !important;
        max-width: 68rem;

        + h1,
        + h2,
        + h3,
        + h4,
        + h5,
        + h6,
        + code,
        + pre,
        + ol,
        + ul {
          margin-top: ${CELL_SPACING.contentVertical} !important;
        }
      }

      a { 
        color: #008abc;
        text-decoration: none;
        &:hover {
          color: #20beff;
        }
      }

      ul,ol {
        padding-left: 1.5rem !important;
        max-width: 68rem;
        li {
          margin: 0 0 ${2.285714 / 4}rem 0;
        }
      }

      ul {
        list-style-type: disc;
      }

      ol {
        list-style-type: decimal;
      }

      b, strong {
        font-weight: 500;
      }

      code {
        background-color: #f8f8f9 !important;
      }
    }
  }

  #notebook-container > div.cell {
    ${styledSourceType("notebook")`
      /* Top and right padding are necessary for multi-cell selection. */
      padding: 5px 5px 0 0;
      border-radius: 4px;
      /* Give some space between cells for multi-cell selections. */
      margin-bottom: 3px;
      padding-right: ${CELL_SPACING.right};
      border-left: 3px solid transparent;
      ${styledMedia(BreakpointUp.XS)`
        margin-left: ${CELL_SPACING.leftMargin};
        margin-right: ${CELL_SPACING.rightMargin};
        width: calc(100% - ${CELL_SPACING.leftMargin} - ${CELL_SPACING.rightMargin});
      `}
      ${styledMedia(BreakpointDown.XS)`
        margin-left: 0;
        margin-right: 0;
        width: 100%;
      `}
    `}
    ${styledSourceType("script")`
      padding: 0;
      border-radius: 0;
      margin: 0;
      width: calc(100% - ${CELL_SPACING.leftMargin});
      border: 0;
    `}
    border-color: transparent;
  }

  #notebook-container > div.cell.code_cell .input, 
  #notebook-container > div.cell.code_cell.unselected .input:hover,
  #notebook-container > div.cell.text_cell.unselected .text_cell_render,
  #notebook-container > div.cell.code_cell.selected .input,
  #notebook-container > div.cell.text_cell.selected .input_area {
    position:relative;

    &:after {
      top: 0;
      height: 100%;
      content: "";
      position: absolute;
    }
  }

  #notebook-container > div.cell.code_cell .input:after {
    left: ${CELL_SPACING.left};

    ${(props: ThemeProps) => {
      if (
        props.theme.session &&
        props.theme.session.kernelSourceType === "notebook"
      ) {
        if (props.theme.name === "dark") {
          return `border-left: 1px solid ${darkPalette.darkLines};`;
        } else {
          return `border-left: 1px solid ${props.theme.legacyColors.border};`;
        }
      } else {
        return "border-left: 0";
      }
    }};
  }

  #notebook-container > div.cell.code_cell.unselected .input:hover:after {
    ${styledSourceType("script")`
      border-left: 0;
    `}
    ${styledSourceType("notebook")`
      border-left: 2px solid rgba(4, 164, 222, 0.5);
    `}
    left: ${CELL_SPACING.left};
  }

  #notebook-container > div.cell.text_cell.unselected .text_cell_render:after {
    /* Keep markdown from jumping around when hovered / selected. */
    border-left: 2px solid transparent;
  }

  #notebook-container > div.cell.text_cell.unselected .text_cell_render:hover:after {
    ${styledSourceType("script")`
      border-left: 0;
    `}
    ${styledSourceType("notebook")`
      border-left: 2px solid rgba(4, 164, 222, 0.5);
    `}
    left: 0;
  }

  /* Blue border on code cell selection. */
  #notebook-container > div.cell.code_cell.selected .input:after {
    ${styledSourceType("script")`
      border-left: 0;
    `}
    ${styledSourceType("notebook")`
      border-left: 2px solid #04A4DE;
    `}
    left: ${CELL_SPACING.left};
  }

  /* Blue border on markdown cell selection. */
  #notebook-container > div.cell.text_cell.selected .input_area:after,
  #notebook-container > div.cell.text_cell.selected.rendered .text_cell_render {
    ${styledSourceType("script")`
      border-left: 0;
    `}
    ${styledSourceType("notebook")`
      border-left: 2px solid #04A4DE;
    `}
    z-index: 3;
    left: 0;
  }

  #notebook-container > div.cell:before {
    display: none;
  }

  /** Make the cell not look too short for the right pane. */
  #notebook-container > div.cell > .input .CodeMirror-code {
    box-sizing: border-box;
  }

  #notebook-container > div.cell > .input .CodeMirror-scroll {
    ${styledSourceType("script")`
      max-height: 100vh;
      max-width: 100vw;
    `}
  }

  #notebook .CodeMirror-scrollbar-filler {
    background: transparent;
  }

  /** Jupyter does funky stuff with the output area, this overrides. */
  #notebook-container > div.cell > .output_wrapper {
    display: ${props =>
      props.theme.session.kernelSourceType === "script" ? "none" : "flex"};
    width: calc(100% + ${CELL_SPACING.rightMargin});
    flex-flow: row nowrap;

    /* Basic attempt at cleaning up bootstrap styles in output. */
    .container {
      padding-right: 0;
      padding-left: 0;
      margin-right: auto;
      margin-left: auto;
    }
  }
  
  /* Set the cell output width to the left + cell width (leave space for custom right). */
  #notebook-container > div.cell > .output_wrapper > .output {
    width: calc(100% - ${CELL_SPACING.rightMargin});
  }

  /* Set the cell output width to the left + cell width (leave space for custom right). */
  #notebook-container > div.cell > .output_wrapper > .output_collapsed {
    /* Required to override Jupyter inline style. */
    display: none !important;
  }

  #notebook-container > div.cell > .output_wrapper > .output > .output_area > pre {
    font-weight: 500;
  }

  /* Fix for pre (code) blocks within a markdown cell. */
  #notebook #notebook-container div.text_cell .rendered_html pre {
    max-width: 100%;
    overflow: auto;
    padding: 0.5rem !important;
  }

  /* Fix for code blocks within a markdown cell. */
  #notebook #notebook-container div.text_cell .rendered_html code {
    white-space: pre;
  }
  
  #notebook-container > div.cell > .output_wrapper > .output > .output_area {
    /* Disable native Jupyters breakpoint that swaps this to column layout (snaps output left on mobile). */
    flex-direction: row !important;
    height: 100%;
  }

  /* Pin the output left area to the normal left spacing. */
  #notebook-container > div.cell > .output_wrapper > .output > .output_area > .prompt {
    margin-top: ${CELL_SPACING.verticalHalf} !important;
    width: ${CELL_SPACING.left};
    min-width: ${CELL_SPACING.left};
  }

  /* Output cell styling. */
  #notebook-container > div.cell > .output_wrapper > .output > .output_area > .output_subarea {
    margin-top: ${CELL_SPACING.verticalHalf} !important;
    min-width: calc(100% - ${CELL_SPACING.left});
    max-width: calc(100% - ${CELL_SPACING.left});
    border-radius: 4px;
    padding: 8px 10px;
    overflow: auto;
  }

  #notebook-container > div.cell > .output_wrapper > .output > .output_area > .output_subarea > p {
    ${readableColor(70)};
  }

  #notebook-container > div.cell > .output_wrapper > .out_prompt_overlay {
    display: none !important;
  }

  #notebook-container div.output_scroll {
    box-shadow: none !important;
  }

  .CodeMirror {
    padding-right: 12px;
    border-width: 0;
  }
  
  /* Padding for the code */
  .CodeMirror-lines {
    padding: 10px 8px;
  }

  .CodeMirror-line > * {
    font-family: "Roboto Mono", monospace;

    > * {
      font-family: "Roboto Mono", monospace;
    }
  }

  .CodeMirror.cm-s-default.CodeMirror-wrap {
    ${styledSourceType("notebook")`
      border-bottom-right-radius: 4px;
    `}
  }

  /* Following styles are mirromark (markdown cells). */
  .cm-s-mirrormark .cm-m-markdown {
    color: #000;
    font-family: sans-serif;
  }
  .cm-s-mirrormark .cm-m-markdown.cm-header {
    font-family: Menlo, Consolas, Monaco, 'Andale Mono', monospace;
    font-weight: bold;
  }
  .cm-s-mirrormark .cm-m-markdown.cm-header-1 {
    font-size: 28px;
  }
  .cm-s-mirrormark .cm-m-markdown.cm-header-2 {
    font-size: 22px;
    /*
    font-size: 18px;
    */
  }
  .cm-s-mirrormark .cm-m-markdown.cm-quote {
    color: #888;
    background-color: rgba(128, 128, 128, 0.05);
    padding: 5px;
  }
  .cm-s-mirrormark .cm-m-markdown.cm-quote,
  .cm-s-mirrormark .cm-m-markdown.cm-string {
    color: #888;
  }
  .cm-s-mirrormark .cm-m-markdown.cm-tag,
  .cm-s-mirrormark .cm-m-markdown.cm-link {
    color: #444;
  }
  .cm-s-mirrormark .cm-m-markdown.cm-comment {
    color: #a50;
    font-family: Menlo, Consolas, Monaco, 'Andale Mono', monospace;
  }
  .cm-s-mirrormark .cm-m-markdown.cm-m-xml,
  .cm-s-mirrormark .cm-m-markdown.cm-bracket {
    color: #170;
    font-family: inherit;
  }
  .cm-s-mirrormark.CodeMirror.CodeMirror-has-preview .CodeMirror-scroll {
    display: none;
  }
  .cm-s-mirrormark.CodeMirror.CodeMirror-has-preview .CodeMirror-preview {
    display: block;
  }
  .cm-s-mirrormark.CodeMirror .CodeMirror-preview {
    display: none;
    /* This was copy pasted from mirrormark repository with important. */
    height: auto !important;
    /* This was copy pasted from mirrormark repository with important. */
    overflow: visible !important;
    padding: 30px 4%;
    box-sizing: border-box;
    font-family: sans-serif;
  }
  .cm-s-mirrormark.CodeMirror .CodeMirror-preview h1,
  .cm-s-mirrormark.CodeMirror .CodeMirror-preview h2,
  .cm-s-mirrormark.CodeMirror .CodeMirror-preview h3 {
    font-family: Menlo, Consolas, Monaco, 'Andale Mono', monospace;
  }
  .cm-s-mirrormark.CodeMirror .CodeMirror-preview pre,
  .cm-s-mirrormark.CodeMirror .CodeMirror-preview code {
    font-family: Menlo, Consolas, Monaco, 'Andale Mono', monospace;
  }
  .cm-s-mirrormark.CodeMirror .CodeMirror-fullscreen .CodeMirror-preview {
    /* This was copy pasted from mirrormark repository with important. */
    height: 100% !important;
    /* This was copy pasted from mirrormark repository with important. */
    overflow: scroll !important;
  }
  .mirrormark-toolbar li > a {
    display: inline-block;
    /*
    font-family: normal normal normal 14px/1 FontAwesome;
    */
    font-size: inherit;
    text-rendering: auto;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    transform: translate(0, 0);
  }
  .mirrormark-toolbar li > a.pull-left {
    margin-right: .3em;
  }
  .mirrormark-toolbar li > a.pull-right {
    margin-left: .3em;
  }
  .mirrormark-toolbar .bold a:before {
    content: "\f032";
  }
  .mirrormark-toolbar .italicize a:before {
    content: "\f033";
  }
  .mirrormark-toolbar .blockquote a:before {
    content: "\f10d";
  }
  .mirrormark-toolbar .strikethrough a:before {
    content: "\f0cc";
  }
  .mirrormark-toolbar .link a:before {
    content: "\f0c1";
  }
  .mirrormark-toolbar .image a:before {
    content: "\f03e";
  }
  .mirrormark-toolbar .unorderedList a:before {
    content: "\f03a";
  }
  .mirrormark-toolbar .orderedList a:before {
    content: "\f0cb";
  }
  .mirrormark-toolbar .fullScreen a:before {
    content: "\f065";
  }
  .mirrormark-toolbar .preview a:before {
    content: "\f15b";
  }
  .cm-s-mirrormark.CodeMirror-fullscreen .mirrormark-toolbar .fullScreen a:before {
    content: "\f066";
  }
  .cm-s-mirrormark.CodeMirror-has-preview .mirrormark-toolbar .preview a:before {
    content: "\f016";
  }
  /* Toolbar Theme */
  .mirrormark-toolbar {
    background: #fff;
    border: 1px solid #dedfe0;
    display: block;
    margin-top: 0;
    margin-bottom: 0;
    padding-left: 0;
    padding-right: 0;
    border-top-right-radius: 4px;
  }
  .mirrormark-toolbar li {
    display: inline-block;
    position: relative;
    z-index: 1;
  }
  .mirrormark-toolbar li.fullScreen,
  .mirrormark-toolbar li.preview {
    display: none; /* added by RM to hide toolbar item */
    float: right;
  }
  .mirrormark-toolbar li.has-nested:after {
    color: #888;
    position: absolute;
    bottom: 3px;
    right: 3px;
    font-size: 11px;
    transform: scale(1.3, 1);
  }
  .mirrormark-toolbar li a {
    background-color: #fff;
    color: #888;
    cursor: pointer;
    display: block;
    font-size: 16px;
    height: 40px;
    line-height: 40px;
    text-align: center;
    transition: color .2s linear;
    width: 40px;
  }
  .mirrormark-toolbar li:hover a,
  .mirrormark-toolbar li a:hover,
  .mirrormark-toolbar li:hover.has-nested:after {
    color: #000;
    text-decoration: none;
  }
  .mirrormark-toolbar li:hover ul {
    display: block;
  }
  .mirrormark-toolbar li ul {
    background: #e5e5e5;
    display: none;
    position: absolute;
    top: 41px;
    width: 150px;
    z-index: 1000;
  }
  .mirrormark-toolbar li ul li {
    float: none;
    width: 100%;
  }
  .mirrormark-toolbar li ul li a {
    background: transparent;
    /* This was copy pasted from mirrormark repository with important. */
    color: #888 !important;
    font-family: "Helvetica Neue", sans-serif;
    font-size: 12px;
    height: auto;
    line-height: normal;
    padding: .25rem .75rem;
    text-align: left;
    width: auto;
  }
  .mirrormark-toolbar li ul li:hover a {
    /* This was copy pasted from mirrormark repository with important. */
    color: black !important;
  }
  .cm-s-mirrormark {
    font: 16px/1.7 Menlo, Consolas, Monaco, 'Andale Mono', monospace;
    box-sizing: border-box;
    height: auto;
    margin: auto;
    position: relative;
    z-index: 0;
  }
  .cm-s-mirrormark .CodeMirror-scroll {
    /* This was copy pasted from mirrormark repository with important. */
    height: auto !important;
    /* This was copy pasted from mirrormark repository with important. */
    overflow: visible !important;
    padding: 30px 4%;
    box-sizing: border-box;
  }
  .cm-s-mirrormark.CodeMirror-fullscreen .CodeMirror-scroll {
    /* This was copy pasted from mirrormark repository with important. */
    height: 100% !important;
    /* This was copy pasted from mirrormark repository with important. */
    overflow: scroll !important;
  }
  .cm-s-mirrormark pre.CodeMirror-placeholder {
    color: #999;
  }
`;
